import "./AttendanceOverview.css";
import { useState, useEffect } from "react";
import { UserAuth } from "../../context/AuthContext";

import HeaderElement from "../../components/HeaderElement/HeaderElement";
import StandardPopup from "../../components/Global/StandardPopup/StandardPopup";
import LoadingCircle from "../../components/Global/LoadingCircle";
import strings from "../../locales/ro.json";
import { useUI } from "../../hooks/usePermission";

import AttendanceList from "../../components/AttendanceOverview/AttendanceList";
import AttendanceMatrix from "../../components/AttendanceOverview/AttendanceMatrix";
import RenameModal from "../../components/AttendanceOverview/RenameModal";
import { useApi } from "../../hooks/useApi";

const t = strings.attendanceOverview;

const statusLabels = {
  VALIDATED: t.statusValidated,
  PENDING: t.statusPending,
  REJECTED: t.statusRejected,
};

const AttendanceOverview = () => {
  const { user } = UserAuth();
  const canManage = useUI('action.attendance.manage');
  const [attendances, setAttendances] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [attendees, setAttendees] = useState({});
  const [loadingAttendees, setLoadingAttendees] = useState(null);
  const [viewMode, setViewMode] = useState("list"); 
  const [matrixData, setMatrixData] = useState(null);
  const [loadingMatrix, setLoadingMatrix] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [popup, setPopup] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [newTitle, setNewTitle] = useState("");

  const [isEditMode, setIsEditMode] = useState(false);

  const { api, authToken } = useApi();

  const showPopup = (message) => setPopup(message);
  const closePopup = () => setPopup(null);

  const fetchHistory = async () => {
    try {
      const res = await api("attendance/history");
      if (Array.isArray(res)) setAttendances(res);
    } catch (err) {
      console.error("Error fetching attendance history:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!authToken) return;
    if (viewMode === "list") {
      fetchHistory();
    } else {
      fetchMatrix();
    }
  }, [user?.accessToken, viewMode]);

  const fetchMatrix = async () => {
    setLoadingMatrix(true);
    try {
      const res = await api("attendance/matrix");
      if (res && res.sessions) {
        setMatrixData(res);
      }
    } catch (err) {
      console.error("Error fetching attendance matrix:", err);
    } finally {
      setLoadingMatrix(false);
    }
  };

  const handleAddPresence = async (attendanceId, userId) => {
    try {
      await api(`attendance/${attendanceId}/user/${userId}`, "POST");
      
      setMatrixData((prev) => {
        if (!prev) return prev;
        const newUsers = prev.users.map((u) => {
          if (u.id === userId) {
            const newPresences = { ...u.presences, [attendanceId]: "VALIDATED" };
            return { ...u, presences: newPresences };
          }
          return u;
        });
        return { ...prev, users: newUsers };
      });
    } catch (err) {
      console.error("Error adding presence:", err);
      showPopup("Eroare la adăugarea prezenței");
    }
  };

  const handleDeletePresence = async (attendanceId, userId) => {
    try {
      await api(`attendance/${attendanceId}/user/${userId}`, "DELETE");
      
      setMatrixData((prev) => {
        if (!prev) return prev;
        const newUsers = prev.users.map((u) => {
          if (u.id === userId) {
            const newPresences = { ...u.presences, [attendanceId]: "ABSENT" };
            return { ...u, presences: newPresences };
          }
          return u;
        });
        return { ...prev, users: newUsers };
      });
    } catch (err) {
      console.error("Error deleting presence:", err);
      showPopup("Eroare la ștergerea prezenței");
    }
  };

  const handleRenameAttendance = async (id, titleText) => {
    try {
      await api(`attendance/${id}`, "PATCH", { title: titleText });
      setMatrixData((prev) => {
        if (!prev) return prev;
        const newSessions = prev.sessions.map((s) =>
          s.id === id ? { ...s, title: titleText } : s
        );
        return { ...prev, sessions: newSessions };
      });
      setRenameTarget(null);
      setNewTitle("");
    } catch (err) {
      console.error("Error renaming attendance:", err);
      showPopup("Eroare la redenumirea prezenței");
    }
  };

  const toggleExpand = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }

    setExpandedId(id);

    if (attendees[id]) return;

    setLoadingAttendees(id);
    try {
      const res = await api(`attendance/${id}/attendees`);
      if (Array.isArray(res)) {
        setAttendees((prev) => ({ ...prev, [id]: res }));
      }
    } catch (err) {
      console.error("Error fetching attendees:", err);
    } finally {
      setLoadingAttendees(null);
    }
  };

  const handleReopen = async (id) => {
    try {
      await api(`attendance/reopen/${id}`, "POST");
      showPopup(t.reopenSuccess);
      await fetchHistory();
    } catch (err) {
      console.error("Error reopening attendance:", err);
      showPopup(t.reopenError);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api(`attendance/${id}`, "DELETE");
      setAttendances((prev) => prev.filter((a) => a.id !== id));
      if (expandedId === id) setExpandedId(null);
      setDeleteTarget(null);
      showPopup(t.deleteSuccess);
    } catch (err) {
      console.error("Error deleting attendance:", err);
      setDeleteTarget(null);
      showPopup(t.deleteError);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return t.today;
    if (date.toDateString() === yesterday.toDateString()) return t.yesterday;

    return date.toLocaleDateString("ro-RO", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString("ro-RO", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <div className="attendanceOverviewWrapper full-height">
        <div className="attendanceOverviewGrid">
          <div className="attendanceOverviewHeader">
            <HeaderElement headerText={t.title} />
          </div>
          <div className="centerContent">
            <LoadingCircle />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`attendanceOverviewWrapper ${viewMode === 'spreadsheet' ? 'full-height' : ''}`}>
      <div className="attendanceOverviewGrid">
        <div className="attendanceOverviewHeader">
          <div className="attendanceHeaderTop">
            <div className="headerWrapper">
              <HeaderElement headerText={t.title} />
            </div>
            <div className="attendanceViewToggle">
              <button
                className={`toggleBtn ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
              >
                {t.viewList}
              </button>
              <button
                className={`toggleBtn ${viewMode === "spreadsheet" ? "active" : ""}`}
                onClick={() => setViewMode("spreadsheet")}
              >
                {t.viewSpreadsheet}
              </button>
            </div>
          </div>
        </div>

        <div className="attendanceOverviewContent">
          {viewMode === "list" ? (
            <AttendanceList
              attendances={attendances}
              canManage={canManage}
              expandedId={expandedId}
              toggleExpand={toggleExpand}
              handleReopen={handleReopen}
              setDeleteTarget={setDeleteTarget}
              loadingAttendees={loadingAttendees}
              attendees={attendees}
              statusLabels={statusLabels}
              t={t}
              formatDate={formatDate}
              formatTime={formatTime}
            />
          ) : (
            <AttendanceMatrix
              matrixData={matrixData}
              loadingMatrix={loadingMatrix}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              canManage={canManage}
              isEditMode={isEditMode}
              setIsEditMode={setIsEditMode}
              setRenameTarget={setRenameTarget}
              setNewTitle={setNewTitle}
              handleAddPresence={handleAddPresence}
              handleDeletePresence={handleDeletePresence}
              t={t}
            />
          )}
        </div>
      </div>

      <StandardPopup
        isOpen={!!popup}
        message={popup || ""}
        onClose={closePopup}
        buttonsEnabled={false}
      />

      <StandardPopup
        isOpen={!!deleteTarget}
        message={t.deleteConfirm}
        yesFcn={() => handleDelete(deleteTarget)}
        noFcn={() => setDeleteTarget(null)}
      />

      <RenameModal
        renameTarget={renameTarget}
        setRenameTarget={setRenameTarget}
        newTitle={newTitle}
        setNewTitle={setNewTitle}
        handleRenameAttendance={handleRenameAttendance}
      />
    </div>
  );
};

export default AttendanceOverview;
